import { FC, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  AutocompleteRenderInputParams,
  Button,
  Divider,
  ElevatedPopper,
  FormInputText,
  Modal,
  Select,
  Svg,
} from '@hopskipdrive/react-components';
import pluralize from 'pluralize';

import { Checkbox, IconButton } from './styles';
import { assignAlertsValidationSchema } from './validationSchema';

interface BulkAssignProps {
  selectedAlertIds: number[];
  employees: Employee[];
  handleAssignAlerts: ({ assigneeId }) => void;
  onClose: () => void;
  assignAlertsResponse: string[];
  isLoading: boolean;
}

export const BulkAssignAlerts: FC<BulkAssignProps> = ({
  selectedAlertIds,
  employees,
  handleAssignAlerts,
  onClose,
  assignAlertsResponse,
  isLoading,
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const divRef = useRef<HTMLDivElement>(null);

  const onCloseActions = () => {
    setIsModalOpen(false);
    onClose();
  };

  return (
    <div ref={divRef}>
      <ElevatedPopper isOpen={selectedAlertIds.length > 0} anchorElRef={divRef.current} placement="bottom">
        <AssignButton selectedAlertCount={selectedAlertIds.length} setIsModalOpen={() => setIsModalOpen(true)} />
        <Divider flexItem style={{ margin: '4px 14px 0', height: 32, width: 1 }} variant="middle" />
        <IconButton onClick={onClose}>
          <Svg icon="close" />
        </IconButton>
      </ElevatedPopper>
      {isModalOpen && (
        <AssignModal
          isOpen={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          handleAssignAlerts={handleAssignAlerts}
          employees={employees}
          isLoading={isLoading}
        />
      )}
      <PostAssignAlertsModal assignAlertsResponse={assignAlertsResponse} onConfirm={onCloseActions} />
    </div>
  );
};

export const AssignAllCheckBox = ({ isCheckAll, handleCheckboxSelection }) => {
  return <Checkbox data-testid="check-all-box" checked={isCheckAll} onClick={handleCheckboxSelection} />;
};

export const AssignCheckBox = ({ id, selectedAlertIds, handleCheckboxSelection }) => {
  return (
    <Checkbox
      data-testid="row-checkbox"
      checked={selectedAlertIds.indexOf(id) !== -1}
      onClick={handleCheckboxSelection}
    />
  );
};

const AssignButton = ({ selectedAlertCount, setIsModalOpen }) => (
  <Button style={{ height: 40 }} color="secondary" onClick={() => setIsModalOpen(true)}>
    {`Assign ${pluralize('Alert', selectedAlertCount, true)}`}
  </Button>
);

const PostAssignAlertsModal = ({ assignAlertsResponse, onConfirm }) => {
  return (
    <Modal open={assignAlertsResponse.length > 0}>
      <Modal.Title>Assignment results</Modal.Title>
      <Modal.Content>{assignAlertsResponse}</Modal.Content>
      <Modal.Actions>
        <Button autoFocus color="primary" onClick={onConfirm}>
          Ok
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

const AssignModal = ({ isOpen, onCancel, handleAssignAlerts, employees, isLoading }) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      assigneeId: '',
    },
    resolver: yupResolver(assignAlertsValidationSchema),
  });

  const availableEmployees = employees.filter(
    (employee) => employee.srsAvailability.status === 'online' || employee.srsAvailability.status === 'unavailable'
  );

  return (
    <Modal open={isOpen}>
      <Modal.Title>Please select a specialist:</Modal.Title>
      <Modal.Content>
        <form id="bulk-assign-alerts-form" onSubmit={handleSubmit(handleAssignAlerts)}>
          <Controller
            control={control}
            name="assigneeId"
            render={({ field: { value, onChange } }) => (
              <Select
                options={availableEmployees.map((employee) => ({
                  value: employee.id.toString(),
                  label: `${employee.firstName} ${employee.lastName}`,
                }))}
                value={value}
                setValue={onChange}
                renderInput={(params: AutocompleteRenderInputParams) => (
                  <FormInputText
                    {...params}
                    variant="outlined"
                    placeholder="Specialist name"
                    fullWidth
                    error={!!errors.assigneeId}
                    helperText={errors.assigneeId?.message}
                  />
                )}
              />
            )}
          />
        </form>
      </Modal.Content>
      <Modal.Actions>
        <Button variant="text" onClick={onCancel} loading={isLoading}>
          Cancel
        </Button>
        <Button form="bulk-assign-alerts-form" type="submit" loading={isLoading}>
          {isLoading ? 'Confirming...' : 'Confirm'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
